import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/common/Layout';
import { bookService } from '../services/bookService';
import { bookRecordService } from '../services/bookRecordService';
import type { Book, BulkUpload, BookRecord } from '../types';
import { BookFormModal } from '../components/admin/BookFormModal';
import { BulkUploadModal } from '../components/admin/BulkUploadModal';
import { BookDetailModal } from '../components/admin/BookDetailModal';
import { IssueBookModal, ReturnBookModal } from '../components/admin/BookRecordModals';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { Notification } from '../components/common/Notification';
import { getImageUrl } from '../utils/imageHelper';
import './BooksPage.css';

const ITEMS_PER_PAGE = 15;

export const BooksPage = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[]>([]);
  const [borrowedBookIds, setBorrowedBookIds] = useState<Set<number>>(new Set());
  const [bookRecords, setBookRecords] = useState<BookRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BookRecord | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  } | null>(null);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [bookToDelete, setBookToDelete] = useState<Book | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadBooks();
  }, []);

  const loadBooks = async () => {
    try {
      setIsLoading(true);
      const [booksData, records] = await Promise.all([
        bookService.getAllBooks({ limit: 1000 }),
        bookRecordService.getAllRecords({ limit: 1000 }),
      ]);
      setBooks(booksData);
      setBookRecords(records);

      // Find books that are currently borrowed
      const borrowedIds = new Set(
        records
          .filter(r => r.status === 'borrowed')
          .map(r => r.book_id)
      );
      setBorrowedBookIds(borrowedIds);
    } catch (error) {
      console.error('Error loading books:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = (book: Book) => {
    setSelectedBook(book);
    setShowIssueModal(true);
  };

  const handleIssueSaved = (record: BookRecord) => {
    setBookRecords([...bookRecords, record]);
    setShowIssueModal(false);
    setSelectedBook(null);
    // Update borrowed book IDs
    const newBorrowedIds = new Set(borrowedBookIds);
    newBorrowedIds.add(record.book_id);
    setBorrowedBookIds(newBorrowedIds);
  };

  const handleReturn = (record: BookRecord) => {
    setSelectedRecord(record);
    setShowReturnModal(true);
  };

  const handleReturnSaved = async (record: BookRecord) => {
    setBookRecords(bookRecords.map(r => (r.id === record.id ? record : r)));
    setShowReturnModal(false);
    setSelectedRecord(null);
    // Update borrowed book IDs
    const newBorrowedIds = new Set(borrowedBookIds);
    newBorrowedIds.delete(record.book_id);
    setBorrowedBookIds(newBorrowedIds);
    
    // Refresh books list to get updated shelf_id
    await loadBooks();
  };

  const handleEdit = (book: Book) => {
    setEditingBook(book);
    setShowModal(true);
  };

  const handleViewDetails = (book: Book) => {
    setSelectedBook(book);
    setShowDetailModal(true);
  };

  const handleDelete = (book: Book) => {
    setBookToDelete(book);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!bookToDelete) return;

    try {
      await bookService.deleteBook(bookToDelete.id);
      setBooks(books.filter(b => b.id !== bookToDelete.id));
      setShowConfirmModal(false);
      setBookToDelete(null);
    } catch (error) {
      console.error('Error deleting book:', error);
      alert('Failed to delete book');
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setBookToDelete(null);
  };

  const handleBookSaved = (book: Book) => {
    if (editingBook) {
      setBooks(books.map(b => (b.id === book.id ? book : b)));
    } else {
      setBooks([...books, book]);
    }
    setShowModal(false);
    setEditingBook(null);
  };

  const handleModalError = (error: { type: 'error'; title: string; message: string }) => {
    setNotification({
      type: 'error',
      title: error.title,
      message: error.message,
    });
  };

  const handleBulkUploadSubmitted = (uploads: BulkUpload[]) => {
    setShowBulkUploadModal(false);

    if (uploads.length === 0) {
      return; // Don't show notification if no uploads
    }

    // Count successful submissions, skipped, and errors
    const successCount = uploads.filter(u => u.status === 'PENDING').length;
    const skippedCount = uploads.filter(u => u.status === 'REJECTED').length;
    const errorCount = uploads.filter(u => u.status === 'ERROR').length;

    // Build the message
    let message = '';
    if (successCount > 0) {
      message = `${successCount} book(s) submitted for verification. They will appear in the Book Table once approved by admin.`;
    }
    if (skippedCount > 0) {
      message += (message ? ' ' : '') + `${skippedCount} book(s) skipped (already exists).`;
    }
    if (errorCount > 0) {
      message += (message ? ' ' : '') + `${errorCount} error(s) occurred. Check Bulk Uploads for details.`;
    }

    setNotification({
      type: errorCount > 0 ? 'warning' : 'success',
      title: successCount > 0 ? 'Bulk Upload Complete!' : 'Upload Errors',
      message: message,
    });
  };

  const filteredBooks = books.filter((book) => {
    // Comprehensive search: title, author, borrower name, book ID, ISBN, category
    const record = bookRecords.find(r => r.book_id === book.id && r.status === 'borrowed');
    const borrowerName = record?.borrower_name;
    const isBorrowed = borrowedBookIds.has(book.id);
    
    if (!searchTerm) {
      const matchesStatus = !statusFilter ||
        (statusFilter === 'available' && !isBorrowed) ||
        (statusFilter === 'borrowed' && isBorrowed);
      return matchesStatus;
    }

    const normalizedSearch = searchTerm.toLowerCase().trim();
    
    // Search in book fields
    const titleMatch = book.title?.toLowerCase().includes(normalizedSearch);
    const authorMatch = book.author_name?.toLowerCase().includes(normalizedSearch);
    const categoryMatch = book.category?.toLowerCase().includes(normalizedSearch);
    const isbnMatch = book.isbn?.toLowerCase().includes(normalizedSearch);
    const bookIdMatch = String(book.id).includes(normalizedSearch);
    const publisherMatch = book.publisher?.toLowerCase().includes(normalizedSearch);
    
    // Search in borrower fields
    const borrowerMatch = borrowerName?.toLowerCase().includes(normalizedSearch);
    const contactMatch = record?.contact_info?.toLowerCase().includes(normalizedSearch);
    
    const matchesSearch = titleMatch || authorMatch || categoryMatch || isbnMatch || 
                          bookIdMatch || publisherMatch || borrowerMatch || contactMatch;
    
    const matchesStatus = !statusFilter ||
      (statusFilter === 'available' && !isBorrowed) ||
      (statusFilter === 'borrowed' && isBorrowed);
    
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredBooks.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBooks = filteredBooks.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (isLoading) {
    return (
      <Layout isAdmin>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading books...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout isAdmin>
      <div className="books-page">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">Book Table</h1>
            <p className="page-subtitle">
              Manage library book inventory, add new titles, and track availability.
            </p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/admin/bulk-upload')}
              title="Go to Bulk Upload Verification"
            >
              <span className="material-symbols-outlined">qr_code_scanner</span>
              <span>Bulk Upload Verification</span>
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditingBook(null);
                setShowModal(true);
              }}
            >
              <span className="material-symbols-outlined">add_circle</span>
              <span>Add New Book</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${statusFilter === '' ? 'active' : ''}`}
              onClick={() => setStatusFilter('')}
            >
              All Books
            </button>
            <button
              className={`filter-tab ${statusFilter === 'available' ? 'active' : ''}`}
              onClick={() => setStatusFilter('available')}
            >
              Available
            </button>
            <button
              className={`filter-tab ${statusFilter === 'borrowed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('borrowed')}
            >
              Borrowed
            </button>
          </div>
          <div className="search-wrapper">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              className="form-input"
              placeholder="Search by title, author, borrower, category, ISBN, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Total Count */}
        <div className="total-count books-count">
          <strong>Total:</strong> {filteredBooks.length} book{filteredBooks.length !== 1 ? 's' : ''} |
          <span className="available-count"> {books.filter(b => !borrowedBookIds.has(b.id)).length} Available</span> |
          <span className="borrowed-count"> {borrowedBookIds.size} Borrowed</span>
        </div>

        {/* Table */}
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Cover</th>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBooks.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-state">
                      No books found
                    </td>
                  </tr>
                ) : (
                  paginatedBooks.map((book, index) => {
                    const isBorrowed = borrowedBookIds.has(book.id);
                    return (
                      <tr
                        key={book.id}
                        className="book-row"
                        onClick={() => handleViewDetails(book)}
                        title="Click to view details"
                      >
                        <td className="font-medium">{startIndex + index + 1}</td>
                        <td className="book-cover-column">
                          {getImageUrl(book.image_path) ? (
                            <img
                              src={getImageUrl(book.image_path)}
                              alt={book.title}
                              className="book-cover-inline"
                              onError={(e) => {
                                const target = e.currentTarget as HTMLImageElement;
                                target.style.display = 'none';
                                const placeholder = target.nextElementSibling as HTMLElement;
                                if (placeholder) {
                                  placeholder.style.display = 'flex';
                                }
                              }}
                            />
                          ) : null}
                          <div className="book-cover-placeholder-inline" style={{ display: getImageUrl(book.image_path) ? 'none' : 'flex' }} title={`${book.title} by ${book.author_name}`}>
                            <span className="material-symbols-outlined">menu_book</span>
                          </div>
                        </td>
                        <td className="book-title-column">
                          <span className="book-title-text">{book.title}</span>
                        </td>
                        <td>{book.author_name}</td>
                        <td>{book.category || '-'}</td>
                        <td>
                          <span className={`status-badge ${isBorrowed ? 'status-borrowed' : 'status-available'}`}>
                            {isBorrowed ? 'Borrowed' : 'Available'}
                          </span>
                        </td>
                        <td>
                          <div className="actions">
                            {isBorrowed ? (
                              <button
                                className="btn btn-ghost action-btn return"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const record = bookRecords.find(r => r.book_id === book.id && r.status === 'borrowed');
                                  if (record) handleReturn(record);
                                }}
                                title="Return"
                              >
                                <span className="material-symbols-outlined">assignment_return</span>
                                <span>Return</span>
                              </button>
                            ) : (
                              <button
                                className="btn btn-ghost action-btn assign"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssign(book);
                                }}
                                disabled={isBorrowed}
                                title={isBorrowed ? 'Cannot assign - already borrowed' : 'Assign'}
                              >
                                <span className="material-symbols-outlined">assignment_add</span>
                                <span>Assign</span>
                              </button>
                            )}
                            <button
                              className="btn btn-ghost action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(book);
                              }}
                            >
                              <span className="material-symbols-outlined">edit</span>
                              <span>Edit</span>
                            </button>
                            <button
                              className="btn btn-ghost action-btn danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(book);
                              }}
                              disabled={isBorrowed}
                              title={isBorrowed ? 'Cannot delete borrowed book' : 'Delete'}
                            >
                              <span className="material-symbols-outlined">delete</span>
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="table-footer">
            <p className="pagination-info">
              Showing <strong>{filteredBooks.length === 0 ? 0 : startIndex + 1}</strong> to{' '}
              <strong>{Math.min(endIndex, filteredBooks.length)}</strong> of{' '}
              <strong>{filteredBooks.length}</strong> results
            </p>
            <div className="pagination-buttons">
              <button 
                className="btn btn-ghost" 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button 
                className="btn btn-ghost"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
        </div>

        {showModal && (
          <BookFormModal
            book={editingBook || undefined}
            onClose={() => {
              setShowModal(false);
              setEditingBook(null);
            }}
            onSaved={handleBookSaved}
            onBulkUpload={() => setShowBulkUploadModal(true)}
            onError={handleModalError}
          />
        )}

        {showBulkUploadModal && (
          <BulkUploadModal
            onClose={() => setShowBulkUploadModal(false)}
            onSubmitted={handleBulkUploadSubmitted}
          />
        )}

        {showDetailModal && selectedBook && (
          <BookDetailModal
            book={selectedBook}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedBook(null);
            }}
          />
        )}

        {showIssueModal && (
          <IssueBookModal
            books={books}
            existingRecords={bookRecords}
            preSelectedBook={selectedBook}
            onClose={() => {
              setShowIssueModal(false);
              setSelectedBook(null);
            }}
            onSaved={handleIssueSaved}
          />
        )}

        {showReturnModal && selectedRecord && (
          <ReturnBookModal
            record={selectedRecord}
            books={books}
            onClose={() => {
              setShowReturnModal(false);
              setSelectedRecord(null);
            }}
            onSaved={handleReturnSaved}
          />
        )}

        {showConfirmModal && bookToDelete && (
          <ConfirmModal
            title="Delete Book"
            message={`Are you sure you want to delete "${bookToDelete.title}"?`}
            warningMessage="This action cannot be reverted."
            confirmText="Delete"
            cancelText="Cancel"
            isDanger
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        )}

        {notification && (
          <Notification
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </Layout>
  );
};
